'use client';

import { ErrorInfo, ReactNode, useMemo } from 'react';

import { AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ToolUIErrorBoundaryProps {
  children: ReactNode;
  toolName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

function ToolUIErrorFallback({ error, toolName }: FallbackProps & { toolName?: string }) {
  const t = useTranslations('toolUIError');
  const { isDebugMode } = useDebugMode();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const showErrorDetails = (isDevelopment || isDebugMode) && !!error;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <Alert variant="destructive" className="my-2">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t('title')}</AlertTitle>
      <AlertDescription>
        <p>{toolName ? t('description', { toolName }) : t('genericDescription')}</p>
        {showErrorDetails && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs hover:underline">{t('showDetails')}</summary>
            <div className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2" dir="ltr">
              <pre className="text-xs break-all whitespace-pre-wrap">
                {errorMessage}
                {errorStack && `\n\n${errorStack}`}
              </pre>
            </div>
          </details>
        )}
      </AlertDescription>
    </Alert>
  );
}

export function ToolUIErrorBoundary({ children, toolName, onError }: ToolUIErrorBoundaryProps) {
  const FallbackComponent = useMemo(
    () =>
      function BoundFallback(props: FallbackProps) {
        return <ToolUIErrorFallback {...props} toolName={toolName} />;
      },
    [toolName],
  );

  return (
    <ErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={(error, errorInfo) => {
        if (error instanceof Error) {
          onError?.(error, errorInfo);
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
